"use client"

import { useEffect } from "react"

const TEXT_ENTRY = "input,textarea,select,[contenteditable]"

// iOS scrolls the page straight through `overflow: hidden` on the body, so the body leaves the
// scroll flow entirely — except while a field holds focus, when a fixed body makes iOS report a
// visual viewport 98px shorter than the one it paints. There the overlay's touch-action holds it.
let depth = 0
let lockedAt = 0
let pinned = false
let frame = 0

const pin = () => {
  if (pinned) return
  pinned = true
  const { style } = document.body
  style.position = "fixed"
  style.top = `${-lockedAt}px`
  style.left = "0"
  style.right = "0"
  style.overflow = "hidden"
}

const unpin = () => {
  if (!pinned) return
  pinned = false
  const { style } = document.body
  style.position = ""
  style.top = ""
  style.left = ""
  style.right = ""
  style.overflow = ""
  window.scrollTo(0, lockedAt)
}

const syncPin = () => {
  frame = 0
  if (document.activeElement?.closest(TEXT_ENTRY)) unpin()
  else pin()
}

const schedulePin = () => {
  if (frame === 0) frame = requestAnimationFrame(syncPin)
}

const lock = () => {
  depth += 1
  if (depth > 1) return
  lockedAt = window.scrollY
  document.documentElement.style.overscrollBehavior = "none"
  syncPin()
  document.addEventListener("focusin", schedulePin)
  document.addEventListener("focusout", schedulePin)
}

const unlock = () => {
  depth -= 1
  if (depth > 0) return
  if (frame !== 0) {
    cancelAnimationFrame(frame)
    frame = 0
  }
  document.removeEventListener("focusin", schedulePin)
  document.removeEventListener("focusout", schedulePin)
  if (pinned) unpin()
  else window.scrollTo(0, lockedAt)
  document.documentElement.style.overscrollBehavior = ""
}

export const useScrollLock = (locked: boolean) => {
  useEffect(() => {
    if (!locked) return
    lock()
    return unlock
  }, [locked])
}

export const effectiveScrollY = (): number => (depth > 0 ? lockedAt : window.scrollY)
